/**
 * ProductRecommendations — client-only CIO recommendations carousel.
 *
 * Architecture: CIO Path A §2 — cart/home/PDP recs are client-SDK-only.
 * Flow: CIO getRecommendations(podId) → product IDs → SCAPI /resources/product-recommendations
 * The two-step fetch lives in the shared `useCioRecs` hook (SFCC-11581).
 *
 * Sources:
 * - hec-composable/hec/components/product-recommendations/productRecommendations.tsx
 * - docs/cio-path-a-architecture.md §2
 */
import { isEditing } from "@builder.io/sdk-react";

import { useCioSdkTrackingEnabled } from "~/hec/analytics/cio-sdk-tracking";
import SvgArrow from "~/hec/assets/icons/arrow";
import { CarouselCta } from "~/hec/components/molecules/carousel-cta/carousel-cta";
import { CarouselHeadline } from "~/hec/components/molecules/carousel-headline/carousel-headline";
import { ProductTileSkeleton } from "~/hec/components/product/product-tile-skeleton";
import { RecsCarouselTile } from "~/hec/components/recs/recs-carousel-tile";
import { useCioRecs } from "~/hec/components/recs/use-cio-recs";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "~/hec/components/ui/carousel";
import { useFetchCioProductCategory } from "~/hec/hooks/constructor-io/useFetchCioProductCategory";
import { useTrackRecsClick } from "~/hec/hooks/use-track-recs-click";
import { useTrackRecsView } from "~/hec/hooks/use-track-recs-view";
import { cioBrowseToHecProduct } from "~/hec/utils/product-adapters";
import { cn } from "~/lib/utils";

import type { ProductRecommendationsProps } from "./types";

const SKELETON_COUNT = 4;

/**
 * Editor-only placeholder (SFCC-11781). The recs carousel is client-SDK-driven
 * and can't resolve inside the Builder Visual Editor iframe (no CIO beacon), so
 * on a CIO error or an empty pod it would render zero-height — leaving authors
 * with an invisible, unselectable block. Reserve a labelled box with height
 * while editing. The live storefront still renders real recs (or collapses on a
 * genuinely empty pod; a CIO error there is already logged + reported
 * client-side via SFCC-10830 telemetry).
 */
function RecsEditorPlaceholder({
  podId,
  heading,
  reason,
  className,
}: {
  podId: string;
  heading?: string;
  reason: string;
  className?: string;
}) {
  return (
    <div
      className={cn("products-recommendations", className)}
      data-testid="product-recommendations-editor-placeholder"
    >
      <div className="flex min-h-[220px] w-full flex-col items-center justify-center gap-1 border-2 border-dashed border-gray-300 bg-gray-50 p-6 text-center text-sm text-gray-500">
        <span className="font-semibold text-gray-700">
          Product Recommendations
        </span>
        <span>Pod: {podId}</span>
        {heading && <span>Heading: {heading}</span>}
        <span>{reason}</span>
        <span className="text-xs">
          Live recommendations render on the published storefront.
        </span>
      </div>
    </div>
  );
}

export function ProductRecommendations({
  podId,
  categoryId,
  itemIds,
  groupId,
  numResults = 10,
  heading,
  className,
  slidesVisible = "4",
  slidesVisibleTablet,
  slidesVisibleMobile = "2",
  showPeek,
  showPeekTablet,
  showPeekMobile,
  slidePadding,
  slidePaddingTablet,
  slidePaddingMobile,
  enableNavigationArrows = true,
  showNavigationArrowsMobile = true,
  showNavigationArrowsTablet = showNavigationArrowsMobile,
  showNavigationArrowsDesktop = true,
  enableBasicView = false,
  productTileBorderColor,
  enableAddToCart,
  enableQuickView,
  enableFavorites,
  enableLowStockBadge,
  enableHeadline = true,
  headingStyle = "bar",
  ButtonNavigationColor,
  ctaText,
  ctaHref,
}: ProductRecommendationsProps) {
  // A breakpoint with no author padding keeps the 20px gutter (-ml-5 / pl-5);
  // a set one uses CarouselItem's px-N, same as ProductCarousel.
  const gutterMobile = slidePaddingMobile === undefined;
  const gutterDesktop = slidePadding === undefined;
  const itemLayout = {
    slidesVisible,
    slidesVisibleTablet,
    slidesVisibleMobile,
    showPeek,
    showPeekTablet,
    showPeekMobile,
    slidePadding: slidePadding ?? "0",
    slidePaddingTablet,
    slidePaddingMobile: slidePaddingMobile ?? "0",
    className: cn(gutterMobile && "pl-5", gutterDesktop && "lg:pl-5"),
  };
  const arrowVisibility = cn(
    !(enableNavigationArrows && showNavigationArrowsMobile) && "max-sm:hidden",
    !(enableNavigationArrows && showNavigationArrowsTablet) &&
      "sm:max-lg:hidden",
    !(enableNavigationArrows && showNavigationArrowsDesktop) && "lg:hidden",
  );
  // The arrow icon draws with currentColor, so `color` tints it with the border.
  const arrowColor = ButtonNavigationColor
    ? { borderColor: ButtonNavigationColor, color: ButtonNavigationColor }
    : undefined;

  // Two at a time, as before, unless a breakpoint shows a single tile: with
  // one visible, scrolling by two would never stop on every second product.
  const slidesToScroll = [
    slidesVisible,
    slidesVisibleTablet,
    slidesVisibleMobile,
  ].some(count => Number(count) < 2)
    ? 1
    : 2;

  const isCategory = typeof categoryId === "string" && categoryId.length > 0;

  // Pod (recommendations) path — the canonical PDP / cart / home / search
  // surface. Disabled when a categoryId is supplied so only one CIO call fires.
  const rec = useCioRecs(podId ?? "", {
    itemIds,
    groupId,
    numResults,
    enabled: !isCategory && !!podId,
  });

  // Legacy MRT category (CIO browse) path. Both hooks are called every render
  // (Rules of Hooks); the inactive one is gated off and issues no request.
  const cat = useFetchCioProductCategory(
    categoryId ?? "",
    numResults,
    isCategory,
  );

  const products = isCategory
    ? cat.products.map(cioBrowseToHecProduct)
    : rec.products;
  const cioHeading = isCategory ? (cat.categoryName ?? undefined) : rec.heading;
  // Category browse swallows failures into an empty result set (no error
  // signal), so treat it as non-erroring and let the empty-state logic collapse
  // it — same as a genuinely empty pod.
  const error = isCategory ? false : rec.error;
  const isLoading = isCategory
    ? cat.isLoading || !cat.hasLoaded
    : rec.isLoading;
  // Identifier used for the editor placeholder + GTM list attribution.
  const listId = podId ?? categoryId ?? "";

  // CIO recommendation_view / recommendation_click (SFCC-11961) — pod path
  // only; the legacy category path is a browse, not a recommendations pod.
  const trackedPodId = !isCategory && podId ? podId : undefined;
  // This carousel never sent the click before; it rides the cutover switch.
  const sdkTrackingEnabled = useCioSdkTrackingEnabled();
  const trackClick = useTrackRecsClick();
  const viewRef = useTrackRecsView({
    podId: trackedPodId,
    resultId: rec.resultId,
    itemIds: isLoading ? [] : products.map(product => product.id),
  });

  // In the Builder Visual Editor, never collapse to zero height — authors must
  // be able to see and position the block even when recs can't resolve in the
  // editor context. On the live storefront a failed/empty pod still collapses
  // (the CIO error is already logged + reported client-side). (SFCC-11781)
  const editing = isEditing();

  if (error) {
    return editing ? (
      <RecsEditorPlaceholder
        podId={listId}
        heading={cioHeading}
        reason="Recommendations unavailable (CIO request failed)."
        className={className}
      />
    ) : null;
  }

  const resolvedHeading = heading ?? cioHeading;
  const showSkeletons = isLoading;
  const showCarousel = !isLoading && products.length > 0;

  if (!showSkeletons && !showCarousel) {
    return editing ? (
      <RecsEditorPlaceholder
        podId={listId}
        heading={cioHeading}
        reason="No recommendations returned for this pod."
        className={className}
      />
    ) : null;
  }

  return (
    <div ref={viewRef} className={cn("products-recommendations", className)}>
      {enableHeadline && resolvedHeading && headingStyle === "bar" && (
        <h4 className="w-full bg-gray-01 py-6 text-center text-base font-semibold text-emphasis">
          {resolvedHeading}
        </h4>
      )}
      {enableHeadline && resolvedHeading && headingStyle === "headline" && (
        <CarouselHeadline className="mb-4">{resolvedHeading}</CarouselHeadline>
      )}
      {/* Non-looping (SFCC-11456): production recs don't wrap, so the prev/next
          arrows must hide at the start/end. `hideWhenDisabled` does that — with
          `loop` the carousel could never reach a "no previous items" state and
          the left arrow stayed visible + clickable. */}
      <Carousel opts={{ slidesToScroll, align: "start" }}>
        <CarouselPrevious
          overlapContent={false}
          hideWhenDisabled
          buttonIcon={SvgArrow}
          iconAttributes={{ className: "h-5 w-[23px] -scale-x-100" }}
          className={cn(
            "left-0 h-[50px] w-[50px] border border-blue-04 bg-white text-blue-04 lg:-left-[25px]",
            arrowVisibility,
          )}
          style={arrowColor}
          data-testid="product-recommendations-nav"
        />
        <CarouselContent
          className={cn(
            gutterMobile && "-ml-5",
            slidePaddingTablet && "sm:ml-0",
            gutterDesktop ? "lg:-ml-5" : "lg:ml-0",
          )}
        >
          {showSkeletons &&
            Array.from({ length: SKELETON_COUNT }).map((_, i) => (
              <CarouselItem key={`skel-${i}`} {...itemLayout}>
                {/* Same box as the loaded tile, so the border shows while loading. */}
                {productTileBorderColor ? (
                  <div
                    className="h-full overflow-hidden rounded-md border-2 border-solid bg-white"
                    style={{ borderColor: productTileBorderColor }}
                  >
                    <ProductTileSkeleton />
                  </div>
                ) : (
                  <ProductTileSkeleton />
                )}
              </CarouselItem>
            ))}
          {showCarousel &&
            products.map((product, i) => (
              <CarouselItem key={`${product.id}-${i}`} {...itemLayout}>
                {/* onClickCapture fires `recommendation_click` before the
                    tile's internal <Link> navigates, without blocking it.
                    Only link clicks count, as on the PLP tile: not the heart,
                    add-to-cart or Quick View buttons. */}
                <div
                  className="h-full"
                  onClickCapture={e => {
                    if (!trackedPodId || !sdkTrackingEnabled) return;
                    if (!(e.target as HTMLElement).closest("a[href]")) return;
                    trackClick({
                      podId: trackedPodId,
                      itemId: product.id,
                      itemName: product.name ?? product.id,
                      strategyId: rec.strategyById.get(product.id),
                      resultId: rec.resultId,
                      resultPositionOnPage: i + 1,
                      numResultsPerPage: products.length,
                    });
                  }}
                >
                  <RecsCarouselTile
                    product={product}
                    gtmListId={listId}
                    gtmListName={resolvedHeading}
                    gtmIndex={i + 1}
                    enableRatings={!enableBasicView}
                    borderColor={productTileBorderColor}
                    enableAddToCart={enableAddToCart}
                    enableQuickView={enableQuickView}
                    enableFavorites={enableFavorites}
                    enableLowStockBadge={enableLowStockBadge}
                  />
                </div>
              </CarouselItem>
            ))}
        </CarouselContent>
        <CarouselNext
          overlapContent={false}
          hideWhenDisabled
          buttonIcon={SvgArrow}
          iconAttributes={{ className: "h-5 w-[23px]" }}
          className={cn(
            "right-0 h-[50px] w-[50px] border border-blue-04 bg-white text-blue-04 lg:-right-[25px]",
            arrowVisibility,
          )}
          style={arrowColor}
          data-testid="product-recommendations-nav"
        />
      </Carousel>
      {ctaText && (
        <CarouselCta
          text={ctaText}
          href={ctaHref}
          data-testid="product-recommendations-cta-button"
        />
      )}
    </div>
  );
}
